import { DATE_FORMAT } from './../../../utils/constants';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { ResponseModel } from '../../models/response/response.model';
import { ServicesModel } from '../../models/services/services.model';
import { AiServicesModel } from '../../models/services/ai-suggestions/ai-services.model';
import { AiServiceItemCategory } from '../../models/services/ai-suggestions/ai-service-item-category.enum';
import { ScheduleServices } from '../../models/schedule/schedule-services.model';
import { RevisionModel } from '../../models/revision/revision.model';
import dayjs from 'dayjs';
import { CampaignModel } from '../../models/campaigns/campaign.model';
import { FordvehicleDetailModel } from '../../models/ford-user-with-vehicles-models/fordVehicleDetail.model';
import { MaintenanceScheduleModel } from '../../models/test/maintenance/maintenance-schedule.model';
import { IolmKitModel } from '../../models/revision/iolm-kit.model';
import { AuthService, FeatureToggleService, FeatureToggleCodes, NewScheduleService } from '@agenda-customer-ui/data-access';
import { API, environment } from '@core/envs';
import { IDealerByLocation } from '@core/interface';
import { AiSuggestionsStateService } from '../ai-suggestions/ai-suggestions-state.service';
import { AiRecommendationsParamsModel } from '../../models/schedule/ai-recommendations-params.model';
import { AiRecommendationsRequestModel, AiRecommendationsResponseModel } from '../../models/schedule/ai-recommendations.model';

@Injectable({
  providedIn: 'root',
})
export class RevisionServicesService {
  scheduleService = inject(NewScheduleService);
  private readonly aiSuggestionsState = inject(AiSuggestionsStateService);

  constructor(private http: HttpClient, private authService: AuthService, private featureToggleService: FeatureToggleService) {}

  // hasRecallBff(vin: string): Observable<ResponseModel<RecallModel[]>> {
  //   const url: string = environment.osbBff.concat(
  //     `/vehicles/${vin}/recalls?country=${this.storage.getItem(
  //       'country'
  //     )}&status=O`
  //   );

  //   return this.http
  //     .get<ResponseModel<RecallModel[]>>(url, {
  //       headers: this.authService.getHeadersForBff(),
  //     })
  //     .pipe(shareReplay(1));
  // }

  getServices(dealerId: number): Observable<ResponseModel<ServicesModel>> {
    return this.getServicesByFeatureFlag(dealerId);
  }

  getServicesByFeatureFlag(dealerId: number): Observable<ResponseModel<ServicesModel>> {
    const useAiSuggestionsEndpoint = this.featureToggleService.list[FeatureToggleCodes.OSR_AI_SUGGESTIONS]?.active ?? false;

    if (useAiSuggestionsEndpoint) {
      return this.getServicesWithAISuggestionsRaw(dealerId).pipe(
        tap((rawResponse) => {
          // Classifica cada sugestão (item_category) antes de limitar/guardar no estado,
          // usada apenas para tagueamento (AFOSR-6080). Não afeta o restante do pipeline,
          // que continua retornando somente revisionHistory + offeredServices.
          const categorizedResponse = this.categorizeSuggestedServices(rawResponse);

          // Guarda o modelo de sugestões (já limitado para exibição) e, separadamente,
          // a lista completa de IDs recomendados pela IA (sem o corte de 5 itens),
          // necessária para a telemetria de /ai-recommendations no último step.
          const limited = this.limitSuggestedServices(categorizedResponse);
          const allRecommendedIds = categorizedResponse?.suggestedServices?.services?.map((s) => s.serviceId) ?? [];
          this.aiSuggestionsState.setSuggestedServicesModel(limited?.suggestedServices ?? null, allRecommendedIds);
        }),
        map((aiResponse) => this.limitSuggestedServices(aiResponse)),
        map((aiResponse) => ({
          httpCode: 200,
          message: 'OK',
          response: {
            revisionHistory: aiResponse?.revisionHistory ?? [],
            offeredServices: aiResponse?.offeredServices ?? [],
          },
        })),
        tap((data) => localStorage.setItem('@Ford-revision-v4', JSON.stringify(data.response)))
      );
    }

    // Feature flag inativa: garante que não sobre estado de sugestões de uma sessão anterior.
    this.aiSuggestionsState.setSuggestedServicesModel(null);

    const { country, vin } = this.authService.urlInfo;
    const api = environment.getRoute(API.transformation);

    const url = `${api}/revisions/v2/services`;
    return this.http.post<ResponseModel<ServicesModel>>(url, { vin }, { params: { country, dealer: dealerId } }).pipe(
      tap((data) => localStorage.setItem('@Ford-revision-v4', JSON.stringify(data.response)))
    );
  }

  // getServicesBff(
  //   dealerId: number,
  //   division = ''
  // ): Observable<ResponseModel<ServicesModel>> {
  //   const url: string = environment.osbBff.concat(
  //     `/vehicles/${this.storage.getItem(
  //       'vin'
  //     )}/revisions?country=${this.storage.getItem(
  //       'country'
  //     )}&dealer=${dealerId}`
  //   );

  //   return this.http.get<ResponseModel<ServicesModel>>(url, {
  //     headers: this.authService.getHeadersForBff(),
  //   });
  // }

  getRevision(month: number, iolm: string, dealerId?: string): Observable<ResponseModel<RevisionModel[]>> {
    const dealer: IDealerByLocation = JSON.parse(localStorage.getItem('@Ford-dealerSelected') as string );
    const id = dealerId ? dealerId : dealer.dealerId;
    const { country, vin } = this.authService.urlInfo;
    const api = environment.getRoute(API.transformation);
    const url = `${api}/revisions/v2`;
    const body = { vin };
    const iolmParam = this.featureToggleService.list[FeatureToggleCodes.STF_IOLM]?.active ? iolm : 'N';

    // NOTE: do NOT persist this response into '@Ford-revision-v4'. That key stores the
    // ServicesModel (with offeredServices + revisionHistory) from getServices().
    // Overwriting it here with RevisionModel[] corrupts step-3 navigation state and
    // breaks the back button (ServicesComponent re-reads offeredServices from that key on init).
    return this.http.post<ResponseModel<RevisionModel[]>>(url, body, { params: { dealer: id, country, month, iolm: iolmParam } });
  }

  // getRevisionFromBff(
  //   months: number
  // ): Observable<ResponseModel<RevisionModel[]>> {
  //   const url: string = environment.osbBff.concat(
  //     `/revisions/quotations?country=${this.storage.getItem(
  //       'country'
  //     )}&vin=${this.storage.getItem('vin')}&months=${months}`
  //   );

  //   return this.http.get<ResponseModel<RevisionModel[]>>(url, {
  //     headers: this.authService.getHeadersForBff(),
  //   });
  // }

  findAllCampaignByParticipant(
    dateSchedule: string,
    fordprotectPlanType: string | undefined,
    revision: number,
    scheduleOrigin: string,
    vehicleModelGroup: string | null,
    modelYear: number,
    revisionPrice: number
  ): Observable<ResponseModel<CampaignModel[]>> {

    if (
      !dateSchedule ||
      !scheduleOrigin ||
      !vehicleModelGroup ||
      !modelYear ||
      revisionPrice === null
    ) {
      console.error('Algum dos valores para bus de campanha está nulo:');
      console.table({
        dateSchedule,
        fordprotectPlanType,
        scheduleOrigin,
        vehicleModelGroup,
        modelYear,
        revisionPrice
      });
      return of({
        httpCode: 200,
        message: "OK",
        response: [],
      });
    }

    const api = environment.getRoute(API.backoffice);
    const url = `${api}/campaigns`;
    const headers = new HttpHeaders({
      country: this.authService.urlInfo.country,
      'content-type': 'application/json',
    });

    interface CampaignRequestBody {
      vin: string;
      vehicleModelGroup: string;
      vehicleModelYear: string;
      dateSchedule: string;
      revision: string;
      scheduleOrigin: string;
      revisionPrice: string;
      protectPlanType?: string;
    }

    const body: CampaignRequestBody = {
      vin: this.authService.urlInfo.vin,
      vehicleModelGroup: String(vehicleModelGroup),
      vehicleModelYear: String(modelYear),
      dateSchedule: dayjs(dateSchedule).format(DATE_FORMAT),
      revision: String(revision),
      scheduleOrigin,
      revisionPrice: String(revisionPrice),
    };

    // Only include protectPlanType when the vehicle actually has a valid Ford
    // Protect plan. Omitting it (instead of defaulting to OTP) prevents
    // ineligible vehicles from receiving Ford Protect-exclusive campaigns.
    if (fordprotectPlanType) {
      body.protectPlanType = fordprotectPlanType;
    }

    return this.http.post<ResponseModel<CampaignModel[]>>(url, body, { headers });
  }

  getMoreItems(vehicleDetail: FordvehicleDetailModel): Observable<ResponseModel<MaintenanceScheduleModel>> {
    const api = environment.getRoute(API.owners);
    const url = `${api}/vehicles/maintenances?vehicleLine=${vehicleDetail.vehicleLineFeatureCode?.toLowerCase()}&modelYear=${vehicleDetail.modelYear}&country=${this.authService.urlInfo.country}`;
    return this.http.get<ResponseModel<MaintenanceScheduleModel>>(url);
  }

  // getMoreItemsBff(
  //   vehicleDetail: FordvehicleDetailModel
  // ): Observable<MaintenanceScheduleModel> {
  //   const url = environment.osbBff.concat(
  //     `/vehicles/maintenance?vehicleLine=${vehicleDetail.vehicleLineFeatureCode?.toLowerCase()}&year=${vehicleDetail.modelYear
  //     }&country=${this.authService.getParameters().country}`
  //   );

  //   return this.http.get<MaintenanceScheduleModel>(url, {
  //     headers: this.authService.getHeadersForBff(),
  //   });
  // }

  getIolmKit(vin: string, months: number, km: number, motor: null | string): Observable<IolmKitModel[]> {
    const api = environment.getRoute(API.maintenance);
    const url = `${api}/v1/maintenance/v2/maintenance-iolm`;
    return this.http.post<IolmKitModel[]>(url, { vin, mes: months, kmAtual: km, engineCode: motor }).pipe(
      tap((response) => response)
    );
  }

  /** Quantidade máxima de sugestões de IA que podem ser exibidas ao cliente (regra de negócio). */
  private static readonly MAX_AI_SUGGESTED_SERVICES = 5;

  /**
   * Chama o endpoint /revisions/v1/services (agenda-maintenance-api).
   *
   * NOTE: este endpoint retorna o payload (AiServicesModel) diretamente no corpo da
   * resposta HTTP — NÃO usa o envelope padrão ResponseModel ({ httpCode, message, response }).
   * Por isso o tipo de retorno é Observable<AiServicesModel>, e não Observable<ResponseModel<AiServicesModel>>.
   */
  getServicesWithAISuggestions(dealerId?: number): Observable<AiServicesModel> {
    return this.getServicesWithAISuggestionsRaw(dealerId).pipe(
      map((response) => this.limitSuggestedServices(response))
    );
  }

  /**
   * Mesma chamada de `getServicesWithAISuggestions`, porém sem aplicar o corte de exibição
   * (MAX_AI_SUGGESTED_SERVICES). Usado internamente por `getServicesByFeatureFlag` para
   * capturar a lista completa de serviços recomendados pela IA antes do corte, necessária
   * para a telemetria de /ai-recommendations.
   */
  private getServicesWithAISuggestionsRaw(dealerId?: number): Observable<AiServicesModel> {
    const { vin, country } = this.authService.urlInfo;
    const dealerFromStorage: IDealerByLocation | null = JSON.parse(localStorage.getItem('@Ford-dealerSelected') as string);
    const divisionFromSession = Number(sessionStorage.getItem('divisionId'));
    const division = Number.isNaN(divisionFromSession) ? 1 : divisionFromSession;

    const api = environment.getRoute(API.maintenance);
    const url = `${api}/revisions/v1/services`;

    const dealer = dealerId ?? dealerFromStorage?.dealerId;

    const body = {
      dealer: dealer !== undefined ? Number(dealer) : undefined,
      country,
      vin,
      division,
      isMsv: 'N'
    };

    return this.http.post<AiServicesModel>(url, body);
  }

  /**
   * Garante que, no máximo, MAX_AI_SUGGESTED_SERVICES sugestões de IA sejam repassadas adiante,
   * conforme regra de negócio (apenas 5 itens devem ser apresentados ao cliente).
   * Não quebra caso `suggestedServices` ou `services` venham ausentes/vazios.
   */
  private limitSuggestedServices(response: AiServicesModel): AiServicesModel {
    const services = response?.suggestedServices?.services ?? [];
    if (services.length <= RevisionServicesService.MAX_AI_SUGGESTED_SERVICES) {
      return response;
    }

    return {
      ...response,
      suggestedServices: {
        ...response.suggestedServices,
        services: services.slice(0, RevisionServicesService.MAX_AI_SUGGESTED_SERVICES),
      },
    };
  }

  /**
   * Classifica cada serviço sugerido pela IA em uma categoria de tagueamento
   * (item_category), cruzando o serviceId com o catálogo completo de serviços
   * retornado no mesmo response (rawResponse.offeredServices), que já contém
   * as informações necessárias (prevMaintenance, serviceName).
   *
   * Usa as MESMAS regras já aplicadas em ServicesComponent para separar
   * Manutenção / Recall / Serviços Adicionais (ver getOptionalServices e
   * getRecall), garantindo que a categoria enviada ao GTM seja consistente
   * com o que o usuário efetivamente vê em cada aba. Não altera nada além
   * de adicionar o campo `itemCategory` em cada item de suggestedServices.services
   * (AFOSR-6080) — não interfere na lógica de exibição/seleção existente.
   */
  private categorizeSuggestedServices(rawResponse: AiServicesModel): AiServicesModel {
    const catalog = rawResponse?.offeredServices ?? [];
    const services = rawResponse?.suggestedServices?.services ?? [];

    if (!services.length) {
      return rawResponse;
    }

    return {
      ...rawResponse,
      suggestedServices: {
        ...rawResponse.suggestedServices,
        services: services.map((service) => ({
          ...service,
          itemCategory: this.resolveItemCategory(service.serviceId, catalog)
        }))
      }
    };
  }

  /**
   * Determina a categoria de um serviço sugerido a partir do catálogo completo.
   * Retorna `undefined` se o serviço não for encontrado no catálogo (não deveria
   * ocorrer, pois toda sugestão da IA deve existir em offeredServices).
   */
  private resolveItemCategory(serviceId: number, catalog: ScheduleServices[]): AiServiceItemCategory | undefined {
    const catalogService = catalog.find((s) => s.serviceId === serviceId);
    if (!catalogService) {
      return undefined;
    }

    if (catalogService.prevMaintenance) {
      return AiServiceItemCategory.MAINTENANCE;
    }

    const serviceNameLower = catalogService.serviceName?.toLowerCase() ?? '';
    const isRecall = serviceNameLower.includes('recall') || serviceNameLower.includes('campañas de servicio');

    return isRecall ? AiServiceItemCategory.RECALL : AiServiceItemCategory.ADDITIONAL_SERVICE;
  }
  
  sendAiRecommendations(
    params: AiRecommendationsParamsModel,
    body?: AiRecommendationsRequestModel
  ): Observable<ResponseModel<AiRecommendationsResponseModel>> {
    const api = environment.getRoute(API.scheduling);
    const url = `${api}/schedules/${params.scheduleId}/ai-recommendations`;

    const { country } = this.authService.urlInfo;
    const dealerFromStorage: IDealerByLocation | null = JSON.parse(localStorage.getItem('@Ford-dealerSelected') as string);
    const dealerId = params.dealerId ?? dealerFromStorage?.dealerId;

    const headers = new HttpHeaders({
      'user-guid': params.userGuid ?? '',
      dealerId: String(dealerId ?? ''),
      countryCode: country ?? '',
    });    

    return this.http.post<ResponseModel<AiRecommendationsResponseModel>>(url, body, { headers });
  }

  getConnectivityStatus(): Observable < ResponseModel < boolean >> {
      const api = environment.getRoute(API.transformation);
      const url = `${api}/tmc/is-connected`;
    
      const { vin, country } = this.authService.urlInfo;
    
      const headers = new HttpHeaders({
        vin,
        country
      });
    
      return this.http.get<ResponseModel<boolean>>(url, { headers })
        .pipe(
          tap(({ response }) => {
            const status = response ? 'CONNECTED' : 'DISCONNECTED';    
            sessionStorage.setItem('connectivityStatus', status);
          }),
          catchError(() => {
            sessionStorage.setItem('connectivityStatus', 'DISCONNECTED');
    
            return of({
              httpCode: 500,
              message: 'Unable to get connectivity status',
              response: false
            });
          })
        );
    }


}
